import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  HsFormFieldModule,
  HsInputModule,
  HsDividerComponent,
  HsDividerModule,
  HsButtonModule,
  HsSelectModule,
  HsIconModule,
  HsSelectOption,
} from '@hsetu/ngui-components';
import { HsLayerModule } from '@hsetu/ngui-resources';
import { DomSanitizer } from '@angular/platform-browser';
import {
  ICON_CHEVRON_RIGHT,
  ICON_HELP,
  ICON_INFO_CIRCLE,
  ICON_OPEN_EXTERNAL,
  ICON_FILE,
} from '@shared/ui/icons/icon-constants';
import { KfmCardComponent } from '@shared/ui/components/features/card/common/kfm-card.component';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { KaufmannSettingsService } from '@modules/settings/services/kfm-settings.service';
import { FormsModule } from '@angular/forms';
import { AuthService } from '@shared/authentication/auth.service';
import { KaufmannSyncService } from '@shared/sync/kfm-sync.service';
import { KfmYesNoDialogComponent } from '@shared/ui/components/features/dialogs/yes-no-dialog/kfm-yes-no-dialog.component';
import {
  COMMON_HELP_DESCRIPTION_LABEL,
  COMMON_HELP_LABEL,
  DATA_PROTECTION_DESCRIPTION_LABEL,
  DATA_PROTECTION_LABEL,
  DELETE_LOCAL_DATA_DESCRIPTION_LABEL,
  DELETE_LOCAL_DATA_LABEL,
  HELP_AND_SUPPORT_LABEL,
  IMPORT_ARTICLE_CATALOGS_DESCRIPTION_LABEL,
  IMPORT_ARTICLE_CATALOGS_LABEL,
  INFORMATION_LABEL,
  MANAGE_ARTICLES_LABEL,
  MANAGE_PROCESSES_LABEL,
  MWST_LABEL,
  SHOW_ACCOMPLISHED_PROCESSES_DESCRIPTION_LABEL,
  SHOW_ACCOMPLISHED_PROCESSES_LABEL,
  SHOW_ARTICLE_IMAGES_DESCRIPTION_LABEL,
  SHOW_ARTICLE_IMAGES_LABEL,
  SHOW_CLOSED_PROCESSES_DESCRIPTION_LABEL,
  SHOW_CLOSED_PROCESSES_LABEL,
  SHOW_LIST_PRICES_DESCRIPTION_LABEL,
  SHOW_LIST_PRICES_LABEL,
  SHOW_MWST_DESCRIPTION_LABEL,
  SHOW_MWST_LABEL,
  SUPPORT_DESCRIPTION_LABEL,
  SUPPORT_LABEL,
  TEAMVIEWER_DESCRIPTION_LABEL,
  TEAMVIEWER_LABEL,
  TERMS_OF_USE_DESCRIPTION_LABEL,
  TERMS_OF_USE_LABEL,
  VERSION_HISTORY_LABEL,
  VERSION_LABEL,
  WHAT_IS_NEW_LABEL,
} from '@modules/settings/i18n/label-text-constants';
import { environment } from '@environments/environment';
import {
  KFM_WEB_HELP_DOCS_URL,
  KFM_WEB_SUPPORT_URL,
  KFM_WEB_WHATS_NEW_URL,
} from '@shared/constants/constants';
import { ItemStockService } from '@modules/shopping/services/item-stock.service';

@Component({
  selector: 'kfm-app-settings-dialog',
  imports: [
    CommonModule,
    FormsModule,
    HsFormFieldModule,
    HsLayerModule,
    HsInputModule,
    KfmCardComponent,
    HsDividerComponent,
    HsDividerModule,
    HsButtonModule,
    HsSelectModule,
    HsIconModule,
  ],
  templateUrl: './kfm-app-settings-dialog.component.html',
  host: { class: 'kfm-app-settings-dialog layer-two' },
})
export class KfmAppSettingsDialogComponent {
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _itemService = inject(ItemStockService);
  private readonly _authService = inject(AuthService);
  private readonly _syncService = inject(KaufmannSyncService);
  private readonly _dialog = inject(Dialog);

  protected readonly settings = inject(KaufmannSettingsService);
  protected readonly dialogRef = inject<DialogRef<void>>(DialogRef<void>);
  protected readonly authenticated = signal<boolean>(false);

  protected readonly okButtonLabel = 'Ok';

  protected readonly processIcon =
    this._sanitizer.bypassSecurityTrustHtml(ICON_FILE);
  protected readonly chevronRightIcon =
    this._sanitizer.bypassSecurityTrustHtml(ICON_CHEVRON_RIGHT);
  protected readonly helpIcon =
    this._sanitizer.bypassSecurityTrustHtml(ICON_HELP);
  protected readonly openExternalIcon =
    this._sanitizer.bypassSecurityTrustHtml(ICON_OPEN_EXTERNAL);
  protected readonly infoIcon =
    this._sanitizer.bypassSecurityTrustHtml(ICON_INFO_CIRCLE);

  protected readonly processCardTitle = MANAGE_PROCESSES_LABEL;
  protected readonly processClosedItemTitle = SHOW_CLOSED_PROCESSES_LABEL;
  protected readonly processClosedItemSubtitle =
    SHOW_CLOSED_PROCESSES_DESCRIPTION_LABEL;
  protected readonly processAccomplishedItemTitle =
    SHOW_ACCOMPLISHED_PROCESSES_LABEL;
  protected readonly processAccomplishedItemSubtitle =
    SHOW_ACCOMPLISHED_PROCESSES_DESCRIPTION_LABEL;

  protected readonly articleCardTitle = MANAGE_ARTICLES_LABEL;
  protected readonly articlePriceItemTitle = SHOW_LIST_PRICES_LABEL;
  protected readonly articlePriceItemSubtitle =
    SHOW_LIST_PRICES_DESCRIPTION_LABEL;
  protected readonly articleMwstItemTitle = SHOW_MWST_LABEL;
  protected readonly articleMwstItemSubtitle = SHOW_MWST_DESCRIPTION_LABEL;
  protected readonly articleMwstLabel = MWST_LABEL;
  protected readonly articleMwstOptions: HsSelectOption<string>[] = [
    '0%',
    '5%',
    '7%',
    '10%',
    '16%',
    '19%',
    '20%',
  ].map((v) => ({ value: v, label: v }));

  protected readonly articleImageItemTitle = SHOW_ARTICLE_IMAGES_LABEL;
  protected readonly articleImageItemSubtitle =
    SHOW_ARTICLE_IMAGES_DESCRIPTION_LABEL;
  protected readonly articleImportItemTitle = IMPORT_ARTICLE_CATALOGS_LABEL;
  protected readonly articleImportItemSubtitle =
    IMPORT_ARTICLE_CATALOGS_DESCRIPTION_LABEL;

  protected readonly helpCardTitle = HELP_AND_SUPPORT_LABEL;
  protected readonly helpDeleteDataItemTitle = DELETE_LOCAL_DATA_LABEL;
  protected readonly helpDeleteDataItemSubtitle =
    DELETE_LOCAL_DATA_DESCRIPTION_LABEL;
  protected readonly helpCommonItemTitle = COMMON_HELP_LABEL;
  protected readonly helpCommonItemSubtitle = COMMON_HELP_DESCRIPTION_LABEL;
  protected readonly helpSupportItemTitle = SUPPORT_LABEL;
  protected readonly helpSupportItemSubtitle = SUPPORT_DESCRIPTION_LABEL;

  protected readonly infoCardTitle = INFORMATION_LABEL;
  protected readonly infoVersionItemTitle = VERSION_LABEL;
  protected readonly infoVersionItemSubtitle = environment.appVersion;
  protected readonly infoNewItemTitle = WHAT_IS_NEW_LABEL;
  protected readonly infoNewItemSubtitle = VERSION_HISTORY_LABEL;
  protected readonly infoDataProtectionItemTitle = DATA_PROTECTION_LABEL;
  protected readonly infoDataProtectionItemSubtitle =
    DATA_PROTECTION_DESCRIPTION_LABEL;
  protected readonly infoUsageConditionsItemTitle = TERMS_OF_USE_LABEL;
  protected readonly infoUsageConditionsItemSubtitle =
    TERMS_OF_USE_DESCRIPTION_LABEL;
  protected readonly infoTeamViewerItemTitle = TEAMVIEWER_LABEL;
  protected readonly infoTeamViewerItemSubtitle = TEAMVIEWER_DESCRIPTION_LABEL;

  constructor() {
    this.settings.loadSettings();

    this._authService.isAuthenticated$.subscribe((authState) => {
      this.authenticated.set(authState.isAuthenticated);
    });
  }

  protected onCheckChanged(event: Event, modelName: string): void {
    const input = event.target as HTMLInputElement;
    const checked = input.checked;

    this.settings[modelName] = checked;
  }

  protected onSelectionChanged(
    selectedOption: HsSelectOption<string>,
    modelName: string,
  ): void {
    this.settings[modelName] = selectedOption.value.replace(
      '%',
      '',
    ) as unknown as number;
  }

  protected async onButtonClicked(option: string): Promise<void> {
    if (option === 'articleImport') {
      this.loadEbCatalog();
    } else if (option === 'helpDeleteData') {
      const dialogRef = this._dialog.open<boolean>(KfmYesNoDialogComponent, {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: {
          title: this.helpDeleteDataItemTitle,
          text: this.helpDeleteDataItemSubtitle,
        },
      });

      dialogRef.closed.subscribe((proceed) => {
        if (proceed) {
          this._syncService.clearLocalData();
        }
      });
    } else if (option === 'helpCommon') {
      window.open(KFM_WEB_HELP_DOCS_URL, '_blank');
    } else if (option === 'helpSupport') {
      window.open(KFM_WEB_SUPPORT_URL, '_blank');
    } else if (option === 'infoNew') {
      window.open(KFM_WEB_WHATS_NEW_URL, '_blank');
    } else if (option === 'infoDataProtection') {
      window.open(
        'https://www.hottgenroth.de/Datenschutzerklaerung.html',
        '_blank',
      );
    } else if (option === 'infoUsageConditions') {
      window.open('https://www.hottgenroth.de/Agb.html', '_blank');
    } else if (option === 'infoTeamViewer') {
      window.open('https://www.teamviewer.com/de/download/windows/', '_blank');
    }
  }

  private async loadEbCatalog(): Promise<void> {
    // Placeholder for future implementation
    const response = await this._itemService.importEbCatalog();

    if (response) {
      // Handle successful import
    } else {
      // Handle import failure
    }
  }
}
